import { DefaultMap } from "@glideapps/ts-necessities";
import fromPairs from "lodash/fromPairs";
import toPairs from "lodash/toPairs";
import type { ChangeObservable } from "../../change-obserable";
import { Watchable } from "../../change-obserable";
import type { Row } from "../../computation-model/data";
import type { RowIndex } from "../../Database";
import type { TableName } from "../../database-strings";
import { logError, logInfo } from "../../debug-print";
import type { TableColumn, TypeSchema } from "../../description";
import type { MaybeForwardee } from "../component-helpers";
import { asArrayValue, ForwardingValueProducer } from "../component-helpers";
import type { Loading, PrimitiveValue } from "../computation-types";
import { LOADING } from "../computation-types";
import { extractActionColumnValuesFromValueProducer, RowData, TableData } from "../data";
import type { AppEnvironment, AppOther, ComputationModel, DataStore, ObjectInspector, ValueProducer } from "../types";
import { Data } from "../types";
import { DatastoreIndexedDBCache } from "./local-datastore-persistence";

function liftPrimitivesToDatas(prims: Record<string, PrimitiveValue>): Record<string, Data> {
    return fromPairs(toPairs(prims).map(([key, value]) => [key, new Data<AppOther, AppEnvironment>(value)]));
}

class LocalDataStoreTableValueProducer extends ForwardingValueProducer<AppOther, AppEnvironment> {
    private currentForwardee: Data | Loading = LOADING;

    constructor(private dataPromise: Promise<Data>) {
        super();
        this.dataPromise
            .then(d => {
                this.currentForwardee = d;
                this.setDirty(this.currentForwardee);
            })
            .catch(e => {
                logError(`Could not load LocalDataStore data: ${e}`);
                this.currentForwardee = new Data<AppOther, AppEnvironment>([]);
                this.setDirty(this.currentForwardee);
            });
    }

    protected recomputeForwardee(): MaybeForwardee<AppOther, AppEnvironment> {
        return this.currentForwardee;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

export class LocalDataStore implements DataStore {
    public readonly persistence: DatastoreIndexedDBCache | undefined;
    private readonly _computationModelObservable: ChangeObservable<ComputationModel | undefined> = new Watchable(
        undefined
    );

    constructor(public readonly appID: string, usePersistence: boolean = true) {
        this.persistence = !usePersistence ? undefined : new DatastoreIndexedDBCache(appID);
    }

    private readonly _dataForTables: DefaultMap<string, TableData<AppOther, AppEnvironment>> = new DefaultMap(
        () => new TableData<AppOther, AppEnvironment>([], undefined)
    );

    private readonly rehydratedTable: DefaultMap<string, boolean> = new DefaultMap(() => false);
    private readonly idsForRows: Map<ValueProducer, number> = new Map();

    private async rehydrateTable(tableName: TableName): Promise<void> {
        if (this.rehydratedTable.get(tableName.name)) return;
        if (this.persistence === undefined) return;

        const rows = this.getDataForTable(tableName);
        for (const { columns, rowID } of await this.persistence.restore(tableName)) {
            const newRow = new RowData(liftPrimitivesToDatas(columns), rows);
            rows.appendItem(newRow);
            if (rowID !== undefined) {
                this.idsForRows.set(newRow, rowID);
            }
        }
        this.rehydratedTable.set(tableName.name, true);
    }

    public setAppEnvironment(_appEnvironment: AppEnvironment): void {
        return;
    }

    public appEnvironmentUpdated(): void {
        return;
    }

    public setAppUser(_appUserID: string): void {
        return;
    }

    private getDataForTable(tableName: TableName): TableData<AppOther, AppEnvironment> {
        return this._dataForTables.get(tableName.name);
    }

    private async fetchTableRowsRehydrating(tableName: TableName): Promise<TableData<AppOther, AppEnvironment>> {
        await this.rehydrateTable(tableName);
        return this.getDataForTable(tableName);
    }

    public fetchTableRows(tableName: TableName): ValueProducer {
        logInfo("Local datastore fetch table rows", this, tableName);
        return new LocalDataStoreTableValueProducer(this.fetchTableRowsRehydrating(tableName));
    }

    public fetchSingleLinkRow(link: ValueProducer): ValueProducer {
        return link;
    }

    public fetchMultipleLinkRows(link: ValueProducer): ValueProducer {
        return link;
    }

    public getFirstRowObservable(): ChangeObservable<Row | undefined> | undefined {
        return undefined;
    }

    public async addRowToTable(
        tableName: TableName,
        columnValuesVP: ValueProducer,
        columnNames: ReadonlySet<string> | undefined
    ): Promise<{ didAdd: boolean; row: ValueProducer | undefined }> {
        logInfo("Local datastore add row", this, tableName);
        const columnValues = await extractActionColumnValuesFromValueProducer(columnValuesVP, columnNames);
        if (columnValues === undefined) {
            logError("Local datastore add row: no column values");
            return { didAdd: false, row: undefined };
        }

        const { datas, values } = columnValues;
        logInfo("Local datastore add row", datas);

        const rows = await this.fetchTableRowsRehydrating(tableName);
        const row = new RowData(datas, rows);
        rows.appendItem(row);
        logInfo("Local datastore added row", rows);

        if (this.persistence !== undefined) {
            const rowID = await this.persistence.persist(tableName, values);
            if (rowID !== undefined) {
                this.idsForRows.set(row, rowID);
            }
        }

        return { didAdd: true, row };
    }

    public async setColumnsInRow(
        _tableName: TableName,
        _rowIndex: RowIndex,
        _columnValuesVP: ValueProducer,
        _withDebounce: boolean
    ): Promise<void> {
        return;
    }

    public async deleteRowAtIndex(
        _tableName: TableName,
        _rowIndex: RowIndex,
        _setUnderlyingData: boolean
    ): Promise<void> {
        return;
    }

    private async deleteFromPersistence(tableName: TableName, row: ValueProducer): Promise<void> {
        if (this.persistence === undefined) return;

        const rowID = this.idsForRows.get(row as Data);
        if (rowID === undefined) return;

        await this.persistence.remove(tableName, rowID);
    }

    public async deleteRow(tableName: TableName, row: ValueProducer): Promise<void> {
        const rows = await this.fetchTableRowsRehydrating(tableName);
        rows.removeItem(row);
        await this.deleteFromPersistence(tableName, row);
    }

    public async clearTable(tableName: TableName): Promise<void> {
        logInfo("Local datastore clear table", this, tableName);
        const rows = await this.fetchTableRowsRehydrating(tableName);
        const arr = asArrayValue(rows.current);
        const len = arr.length;
        for (let i = 0; i < len; i++) {
            const row = rows.removeLastItemWithoutUpdate();
            if (row !== undefined) {
                await this.deleteFromPersistence(tableName, row);
            }
        }
        rows.setListenersDirty();
    }

    public modifyComputedColumns(
        _removed: ReadonlyMap<string, readonly string[]>,
        _modified: ReadonlyMap<string, readonly TableColumn[]>
    ): void {
        return;
    }

    public setSchema(_schema: TypeSchema): void {
        return;
    }

    public userProfileTableChanged(): void {
        return;
    }

    public get schema(): TypeSchema {
        return {} as unknown as TypeSchema;
    }

    public setEmailOwnersColumns(_tableName: TableName, _emailOwnersColumns: readonly string[]): void {
        return;
    }

    public addComputedColumnsToData(_tableName: TableName, _data: Data): void {
        return;
    }

    public isRowOwnedByUser(_tableName: TableName, _row: Row): boolean {
        return true;
    }

    public resetFromUpstream(): void {
        return;
    }

    public getComputationModelObservable(): ChangeObservable<ComputationModel | undefined> {
        return this._computationModelObservable;
    }
}
